import { Scene } from './Scene';
import { ResolvedPrimitive } from '../gltf';
import { GltfMesh } from '../mesh';
import { RenderObject } from '../object/RenderObject';
import { TerrainMaterial } from '../material';
import { VolumeHeightSample } from '../spatial';
import { HeightField, HeightmapHeightField } from './heightfield';
import { TileMeshClient } from './terrain/TileMeshClient';
import { TileMeshOutput } from './terrain/TileMeshGenerator';
import { TerrainSurfaceRegionRef } from './SceneManifest';
import { SurfaceIndexMap } from './terrain/SurfaceIndexMap';
import { SurfaceTextureManager } from './terrain/SurfaceTextureManager';
import {
  TEXELS_PER_METER,
  aabbOverlapsTile,
  changedRegionAabbs,
  rasterizeTile,
  regionOverlapsTile,
} from './terrain/SurfaceRegionRasterizer';

type HeightmapGrid = { data: Uint8Array; width: number; height: number };

/**
 * Self-contained heightmap snapshot — shippable to a worker via structured
 * clone (or transferable) so terrain seeding in the volgrid generator can run
 * off the main thread.
 */
export interface TerrainHeightmapPayload {
  data: Uint8Array;
  width: number;
  height: number;
  amplitude: number;
  worldMinX: number;
  worldMinZ: number;
  worldWidth: number;
  worldDepth: number;
}

type LoadedTile = {
  obj: RenderObject;
  mesh: GltfMesh;
  lod: number;
  /**
   * Surface ids the tile's index map references. Acquired via
   * {@link SurfaceTextureManager.acquire} on load; released on eviction so the
   * manager's refcounts stay honest. Updated when {@link updateSurfaceRegions}
   * re-rasterizes this tile.
   */
  surfaceIds: Set<string>;
  /**
   * Previous-LOD tile fading out during a crossfade. The fade-in tile lives
   * in `obj`/`mesh`; `prev` is destroyed when `tick()` sees the transition
   * complete.
   */
  prev?: { obj: RenderObject; mesh: GltfMesh; lod: number };
  /**
   * Distance-anchored crossfade state. `startDist` is the chebyshev world
   * distance from camera to tile center at the moment the transition began;
   * `sign` is +1 when the new LOD is coarser (camera moving away) or −1 when
   * finer (camera moving closer). Fade progresses with `sign * (d - startDist)`.
   */
  transition?: { startDist: number; sign: 1 | -1 };
};

/**
 * Distance the camera must travel (chebyshev world units) past the LOD-band
 * boundary before the crossfade is fully complete.
 */
const FADE_DISTANCE_METERS = 100;

/**
 * Concentric LOD bands keyed by chebyshev distance from the camera tile.
 *
 *  - LOD0 covers a 9×9 inner ring
 *  - LOD1 extends to a 13×13 ring
 *  - LOD2 extends to a 17×17 ring
 *  - LOD3 extends to a 21×21 ring (≈ 441 tiles total at the largest band)
 */
const LOD_BANDS = [
  { maxDistance: 4, lodLevel: 0 },
  { maxDistance: 6, lodLevel: 1 },
  { maxDistance: 8, lodLevel: 2 },
  { maxDistance: 10, lodLevel: 3 },
] as const;

/**
 * Outermost chebyshev distance any terrain LOD reaches. Beyond this radius the
 * terrain is culled — anything else camera-tracked (scatter, decals, particles)
 * should cap its own ring at the same value to avoid floating-content artifacts
 * over a culled void.
 */
export const TERRAIN_MAX_RING_DISTANCE = LOD_BANDS[LOD_BANDS.length - 1].maxDistance;
const MAX_RING_DISTANCE = TERRAIN_MAX_RING_DISTANCE;

const tileKey = (tx: number, tz: number): string => `${tx},${tz}`;

/**
 * Camera-driven tiled terrain streamer with concentric LOD rings.
 *
 * Tile meshes are generated client-side by a {@link TileMeshClient} (Web Worker)
 * — the streamer no longer fetches per-tile or whole-world bundle GLBs. The
 * shared {@link TerrainMaterial} (PBR textures + slot metadata) is still loaded
 * once at scene activation from the manifest's shared GLB.
 *
 * The LOD ring logic, fade transitions, and eviction are unchanged from the
 * fetch-based predecessor.
 */
export class TiledTerrainStreamer {
  private loaded = new Map<string, LoadedTile>();

  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;
  private worldWidth: number;
  private worldDepth: number;
  private maxLodLevel: number;

  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;
  private lastCameraX = 0;
  private lastCameraZ = 0;

  private heightField: HeightField | null = null;

  /**
   * Raw heightmap + bounds — kept so the volgrid worker can sample terrain
   * directly without main-thread roundtrips. Null when no heightmap was
   * supplied (noise-only scenes).
   */
  private heightmapPayload: TerrainHeightmapPayload | null = null;

  constructor(
    private scene: Scene,
    private meshClient: TileMeshClient,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    worldWidth: number,
    worldDepth: number,
    tilesX: number,
    tilesZ: number,
    maxLodLevel: number,
    private surfaceManager: SurfaceTextureManager,
    private sharedMaterial: TerrainMaterial,
    heightmap: HeightmapGrid | null = null,
    heightmapAmplitude: number = 0,
    heightmapCellSize: number = 0,
    private surfaceRegions: TerrainSurfaceRegionRef[] = [],
    private defaultSurfaceId: string | undefined = undefined,
  ) {
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.worldWidth = worldWidth;
    this.worldDepth = worldDepth;
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.maxLodLevel = maxLodLevel;
    if (heightmap) {
      this.heightmapPayload = {
        data: heightmap.data,
        width: heightmap.width,
        height: heightmap.height,
        amplitude: heightmapAmplitude,
        worldMinX,
        worldMinZ,
        worldWidth,
        worldDepth,
      };
      this.heightField = new HeightmapHeightField({
        heightmap,
        amplitude: heightmapAmplitude,
        worldMinX,
        worldMinZ,
        worldWidth,
        worldDepth,
        cellSize: heightmapCellSize,
        tileWorldSize,
        tilesX,
        tilesZ,
      });
    }
  }

  /**
   * Returns the streamer's heightfield (heightmap-driven). Null when the scene
   * has no authored heightmap (noise-only terrain). The asset-manager's
   * `TerrainHeightOracle` consumes this for editor-time height queries; runtime
   * physics in playful uses {@link sampleHeight} directly.
   */
  getHeightField(): HeightField | null {
    return this.heightField;
  }

  /**
   * Snapshot of the heightmap for off-main-thread consumers (volgrid worker).
   * Returns null for noise-only scenes. The caller is responsible for cloning
   * the `data` Uint8Array if transferring its buffer to a worker.
   */
  getHeightmapPayload(): TerrainHeightmapPayload | null {
    return this.heightmapPayload;
  }

  /**
   * Sample terrain height at (x, z). When `heightmapCellSize > 0`, snaps to the
   * terrain vertex grid and bilinearly interpolates between the four surrounding
   * vertex heights — matching the piecewise-linear surface the GPU renders exactly.
   * Returns null when no heightmap is available (noise terrain).
   */
  sampleHeight(x: number, z: number): number | null {
    return this.heightField ? this.heightField.sampleHeight(x, z) : null;
  }

  /** Sample height + analytic surface normal via central differences. */
  sampleHeightWithNormal(x: number, z: number): VolumeHeightSample | null {
    if (!this.heightField) return null;
    const sample = this.heightField.sampleHeightWithNormal(x, z);
    if (!sample) return null;
    return { height: sample.height, normal: sample.normal };
  }

  /**
   * Generate the terrain mesh for tile (tx, tz) at LOD 0 — same call the
   * streamer's render path uses. Routed through the (optionally worker-backed)
   * `TileMeshClient`, so callers that need terrain geometry off the main
   * thread automatically benefit when the worker is wired up. Used by the
   * BVH-backed physics path to build terrain BLAS that matches what's drawn.
   */
  generateTerrainMeshForTile(tx: number, tz: number): Promise<TileMeshOutput> {
    const tileMinX = this.worldMinX + tx * this.tileWorldSize;
    const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
    const tileMaxX = Math.min(tileMinX + this.tileWorldSize, this.worldMinX + this.worldWidth);
    const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, this.worldMinZ + this.worldDepth);
    return this.meshClient.generate(
      {
        tileX: tx,
        tileZ: tz,
        worldMinX: tileMinX,
        worldMinZ: tileMinZ,
        worldMaxX: tileMaxX,
        worldMaxZ: tileMaxZ,
      },
      0,
    );
  }

  /** The shared surface texture manager — exposed so the editor can `invalidate` on surface edits. */
  getSurfaceManager(): SurfaceTextureManager {
    return this.surfaceManager;
  }

  /** Block on the inner 3×3 (LOD0) around (x, z) so the player never spawns into empty terrain. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;
    this.lastCameraX = x;
    this.lastCameraZ = z;

    const blockingPromises: Promise<void>[] = [];
    const backgroundLaunches: (() => void)[] = [];
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        if (cheb <= 1) {
          blockingPromises.push(this.loadTile(ntx, ntz, lod, false));
        } else {
          backgroundLaunches.push(() => void this.loadTile(ntx, ntz, lod, false));
        }
      }
    }
    await Promise.all(blockingPromises);
    for (const launch of backgroundLaunches) launch();
  }

  /** Per-frame hook: re-evaluate concentric LOD rings. No-op when the camera tile hasn't changed. */
  updateCamera(x: number, z: number): void {
    this.lastCameraX = x;
    this.lastCameraZ = z;
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);

    if (tx === this.lastCameraTileX && tz === this.lastCameraTileZ) return;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const needed = new Map<string, number>();
    for (let dx = -MAX_RING_DISTANCE; dx <= MAX_RING_DISTANCE; dx++) {
      for (let dz = -MAX_RING_DISTANCE; dz <= MAX_RING_DISTANCE; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (!this.inRange(ntx, ntz)) continue;
        const cheb = Math.max(Math.abs(dx), Math.abs(dz));
        const lod = this.lodForDistance(cheb);
        if (lod < 0) continue;
        needed.set(tileKey(ntx, ntz), lod);
      }
    }

    for (const [key, entry] of [...this.loaded.entries()]) {
      const want = needed.get(key);
      if (want === undefined) {
        this.evictTile(key);
      } else if (want !== entry.lod) {
        // Old LOD stays loaded; loadTile will swap it.
      }
    }

    for (const [key, lod] of needed) {
      const have = this.loaded.get(key);
      if (have && have.lod === lod) continue;
      const [ntxStr, ntzStr] = key.split(',');
      void this.loadTile(Number(ntxStr), Number(ntzStr), lod, true);
    }
  }

  /**
   * Update surface regions and re-rasterize only the tiles affected by changed
   * regions. Diffs against the previous region list (kept in
   * `this.surfaceRegions`) and skips tiles whose AABB doesn't intersect any
   * changed region — this is the difference between "re-rasterize 80 tiles per
   * drag tick at 60 Hz" and "re-rasterize the 2-3 tiles the polygon actually
   * touches". A change in `defaultSurfaceId` invalidates every tile (the
   * default fills every uncovered texel) and falls back to the full pass.
   */
  public updateSurfaceRegions(
    regions: TerrainSurfaceRegionRef[],
    defaultSurfaceId: string | undefined,
  ): void {
    const previousRegions = this.surfaceRegions;
    const previousDefault = this.defaultSurfaceId;
    this.surfaceRegions = regions;
    this.defaultSurfaceId = defaultSurfaceId;

    const defaultChanged = defaultSurfaceId !== previousDefault;
    const dirtyAabbs = defaultChanged ? null : changedRegionAabbs(previousRegions, regions);
    // Nothing changed → nothing to do.
    if (dirtyAabbs && dirtyAabbs.length === 0) return;

    const gl = this.scene.ctx.gl;
    const layerMap = this.buildLayerMap(regions, defaultSurfaceId);
    const defaultLayer = this.surfaceManager.getLayerIndex(defaultSurfaceId);
    for (const [key, tile] of this.loaded) {
      const [txStr, tzStr] = key.split(',');
      const tx = Number(txStr);
      const tz = Number(tzStr);
      const tileMinX = this.worldMinX + tx * this.tileWorldSize;
      const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
      const tileMaxX = tileMinX + this.tileWorldSize;
      const tileMaxZ = tileMinZ + this.tileWorldSize;
      const tileBounds = { worldMinX: tileMinX, worldMinZ: tileMinZ, worldMaxX: tileMaxX, worldMaxZ: tileMaxZ };

      // Skip tiles untouched by any changed region (unless the default-fill
      // itself changed, in which case every tile needs a redraw).
      if (dirtyAabbs && !dirtyAabbs.some((a) => aabbOverlapsTile(a, tileBounds))) continue;

      const relevantRegions = regions.filter((r) => regionOverlapsTile(r, tileBounds));
      const mapData = rasterizeTile(
        tileBounds,
        relevantRegions,
        layerMap,
        defaultLayer,
        TEXELS_PER_METER,
        defaultSurfaceId ?? null,
      );
      // Diff the new surface set against the cached one and acquire/release
      // per the difference; refcounts stay correct without leaking layers.
      this.reconcileSurfaceRefs(tile, mapData.surfaceIds);
      for (const tileObj of [tile.obj, tile.prev?.obj]) {
        if (!tileObj) continue;
        const newMap = new SurfaceIndexMap(gl, mapData);
        this.destroyTileIndexMap(tileObj);
        tileObj.terrainSurfaceIndices = newMap.indicesTex;
        tileObj.terrainSurfaceIndicesTexture = newMap.indicesWrapper;
        tileObj.terrainSurfaceWeight = newMap.weightTex;
        tileObj.terrainSurfaceWeightTexture = newMap.weightWrapper;
        tileObj.terrainIndexMapBounds = [tileMinX, tileMinZ, 1 / (tileMaxX - tileMinX), 1 / (tileMaxZ - tileMinZ)];
      }
    }
  }

  /**
   * Build a surfaceId → layer index map for the rasterizer. Wraps
   * {@link SurfaceTextureManager.getLayerIndex} so unknown surfaces (e.g. ones
   * added mid-session and not yet registered) fall back to layer 0.
   */
  private buildLayerMap(
    regions: TerrainSurfaceRegionRef[],
    defaultSurfaceId: string | undefined,
  ): Map<string, number> {
    const m = new Map<string, number>();
    if (defaultSurfaceId) m.set(defaultSurfaceId, this.surfaceManager.getLayerIndex(defaultSurfaceId));
    for (const r of regions) {
      if (!r.surfaceId) continue;
      m.set(r.surfaceId, this.surfaceManager.getLayerIndex(r.surfaceId));
    }
    return m;
  }

  /**
   * Diff the new surfaceId set against [tile.surfaceIds] and acquire/release on
   * the manager so refcounts stay accurate after region edits. Updates the
   * tile's cached set in place.
   */
  private reconcileSurfaceRefs(tile: LoadedTile, nextIds: Set<string>): void {
    for (const id of nextIds) {
      if (!tile.surfaceIds.has(id)) this.surfaceManager.acquire(id);
    }
    for (const id of tile.surfaceIds) {
      if (!nextIds.has(id)) this.surfaceManager.release(id);
    }
    tile.surfaceIds = nextIds;
  }

  /** Per-frame crossfade driver. Also flushes any queued surface-texture mipmap rebuilds. */
  public tick(cameraX: number, cameraZ: number): void {
    this.lastCameraX = cameraX;
    this.lastCameraZ = cameraZ;
    for (const [key, entry] of this.loaded) {
      if (!entry.prev || !entry.transition) continue;
      const fade = this.computeFade(key, entry.transition);
      if (fade >= 1) {
        this.scene.objectManager.removeObject(entry.prev.obj);
        this.destroyTileIndexMap(entry.prev.obj);
        entry.prev.mesh.destroy(this.scene.ctx.gl);
        entry.prev = undefined;
        entry.transition = undefined;
        entry.obj.ditherFade = null;
      } else {
        entry.prev.obj.ditherFade = fade;
        entry.obj.ditherFade = -fade;
      }
    }
    this.surfaceManager.flushMipmaps();
  }

  private cameraDistanceToTile(tx: number, tz: number): number {
    const cx = this.worldMinX + (tx + 0.5) * this.tileWorldSize;
    const cz = this.worldMinZ + (tz + 0.5) * this.tileWorldSize;
    return Math.max(Math.abs(this.lastCameraX - cx), Math.abs(this.lastCameraZ - cz));
  }

  private computeFade(key: string, transition: { startDist: number; sign: 1 | -1 }): number {
    const [txStr, tzStr] = key.split(',');
    const d = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const f = (transition.sign * (d - transition.startDist)) / FADE_DISTANCE_METERS;
    return Math.min(1, Math.max(0, f));
  }

  private inRange(tx: number, tz: number): boolean {
    return tx >= 0 && tx < this.tilesX && tz >= 0 && tz < this.tilesZ;
  }

  private lodForDistance(cheb: number): number {
    for (const band of LOD_BANDS) {
      if (cheb <= band.maxDistance) {
        return Math.min(band.lodLevel, this.maxLodLevel);
      }
    }
    return -1;
  }

  /** Build a `ResolvedPrimitive` from the worker's typed-array output. */
  private buildPrimitive(
    out: TileMeshOutput,
    tx: number,
    tz: number,
    lod: number,
  ): ResolvedPrimitive {
    return {
      attributes: {
        POSITION: { array: out.positions, type: 'VEC3' },
        NORMAL: { array: out.normals, type: 'VEC3' },
        TEXCOORD_0: { array: out.uvs, type: 'VEC2' },
      },
      indices: out.indices,
      extras: {
        _TERRAIN_TILE: true,
        tileX: tx,
        tileZ: tz,
        lodLevel: lod,
      },
    };
  }

  private async loadTile(
    tx: number,
    tz: number,
    lod: number,
    allowTransition: boolean,
  ): Promise<void> {
    const key = tileKey(tx, tz);
    const clampedLod = Math.min(lod, this.maxLodLevel);

    const tileMinX = this.worldMinX + tx * this.tileWorldSize;
    const tileMinZ = this.worldMinZ + tz * this.tileWorldSize;
    const tileMaxX = Math.min(tileMinX + this.tileWorldSize, this.worldMinX + this.worldWidth);
    const tileMaxZ = Math.min(tileMinZ + this.tileWorldSize, this.worldMinZ + this.worldDepth);

    let out: TileMeshOutput;
    try {
      out = await this.meshClient.generate(
        {
          tileX: tx,
          tileZ: tz,
          worldMinX: tileMinX,
          worldMinZ: tileMinZ,
          worldMaxX: tileMaxX,
          worldMaxZ: tileMaxZ,
        },
        clampedLod,
      );
    } catch (e) {
      console.warn(`Failed to generate terrain tile (${tx},${tz}) lod${clampedLod}:`, e);
      return;
    }

    // Camera may have moved during the await; drop if this tile no longer wants
    // exactly this LOD.
    if (this.lodForCurrentCamera(tx, tz) !== lod) return;

    const prim = this.buildPrimitive(out, tx, tz, clampedLod);
    const gltfMesh = new GltfMesh(
      `_terrain_${tx}_${tz}_lod${clampedLod}`,
      this.scene.ctx,
      this.scene,
      prim,
    );
    const obj = this.scene.objectFactory.instantiateMesh(gltfMesh);
    obj.material = this.sharedMaterial;
    // Index map bounds: (worldMinX, worldMinZ, invWidth, invDepth) for the tile.
    obj.terrainIndexMapBounds = [
      tileMinX,
      tileMinZ,
      1 / (tileMaxX - tileMinX),
      1 / (tileMaxZ - tileMinZ),
    ];

    // Rasterize per-tile surface region index map.
    const tileBounds = { worldMinX: tileMinX, worldMinZ: tileMinZ, worldMaxX: tileMaxX, worldMaxZ: tileMaxZ };
    const relevantRegions = this.surfaceRegions.filter((r) => regionOverlapsTile(r, tileBounds));
    const layerMap = this.buildLayerMap(relevantRegions, this.defaultSurfaceId);
    const defaultLayer = this.surfaceManager.getLayerIndex(this.defaultSurfaceId);
    const mapData = rasterizeTile(
      tileBounds,
      relevantRegions,
      layerMap,
      defaultLayer,
      TEXELS_PER_METER,
      this.defaultSurfaceId ?? null,
    );
    const indexMap = new SurfaceIndexMap(this.scene.ctx.gl, mapData);
    obj.terrainSurfaceIndices = indexMap.indicesTex;
    obj.terrainSurfaceIndicesTexture = indexMap.indicesWrapper;
    obj.terrainSurfaceWeight = indexMap.weightTex;
    obj.terrainSurfaceWeightTexture = indexMap.weightWrapper;

    // Acquire surface texture refs so the manager fetches the PNGs (if not
    // already cached) before this tile's first paint.
    for (const sid of mapData.surfaceIds) {
      if (sid.length > 0) this.surfaceManager.acquire(sid);
    }

    this.scene.instanceManager.registerObject(obj);

    this.installLoadedTile(key, obj, gltfMesh, clampedLod, mapData.surfaceIds, allowTransition);
  }

  private installLoadedTile(
    key: string,
    obj: RenderObject,
    gltfMesh: GltfMesh,
    lod: number,
    surfaceIds: Set<string>,
    allowTransition: boolean,
  ): void {
    const existing = this.loaded.get(key);
    const crossfadeOn = this.scene.ctx.engine.graphicsSettings?.lodCrossfadeEnabled ?? false;
    const useTransition = allowTransition && crossfadeOn && !!existing;

    if (!existing) {
      this.loaded.set(key, { obj, mesh: gltfMesh, lod, surfaceIds });
      return;
    }

    if (!useTransition) {
      this.releaseTileSurfaces(existing);
      this.scene.objectManager.removeObject(existing.obj);
      this.destroyTileIndexMap(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      if (existing.prev) {
        this.scene.objectManager.removeObject(existing.prev.obj);
        this.destroyTileIndexMap(existing.prev.obj);
        existing.prev.mesh.destroy(this.scene.ctx.gl);
      }
      this.loaded.set(key, { obj, mesh: gltfMesh, lod, surfaceIds });
      return;
    }

    if (existing.prev && existing.prev.lod === lod) {
      // Camera reversed mid-fade and re-requested the same LOD that's still
      // fading out: cancel the transition, restore prev as the active tile.
      // The new `obj` carries its own freshly-acquired surface refs that
      // never made it onto the screen — release them now since we're
      // discarding `obj`.
      this.releaseSurfaceIds(surfaceIds);
      this.scene.objectManager.removeObject(existing.obj);
      this.destroyTileIndexMap(existing.obj);
      existing.mesh.destroy(this.scene.ctx.gl);
      existing.prev.obj.castsShadows = true;
      existing.prev.obj.ditherFade = null;
      this.loaded.set(key, {
        obj: existing.prev.obj,
        mesh: existing.prev.mesh,
        lod: existing.prev.lod,
        // existing.surfaceIds was acquired alongside existing.obj which we
        // also discard. The prev tile's refs live in existing.surfaceIds
        // — they stay valid here.
        surfaceIds: existing.surfaceIds,
      });
      this.scene.objectManager.removeObject(obj);
      this.destroyTileIndexMap(obj);
      gltfMesh.destroy(this.scene.ctx.gl);
      return;
    }
    if (existing.prev) {
      this.scene.objectManager.removeObject(existing.prev.obj);
      this.destroyTileIndexMap(existing.prev.obj);
      existing.prev.mesh.destroy(this.scene.ctx.gl);
    }
    // The existing tile is fading out → release its surface refs once the
    // visual crossfade completes. For simplicity we release now: refcounts
    // are non-negative integers and the manager keeps fetched layers cached
    // at zero, so a brief drop is harmless and avoids tracking refs across
    // the asynchronous fade.
    this.releaseTileSurfaces(existing);
    existing.obj.ditherFade = null;
    existing.obj.castsShadows = false;
    const newPrev = { obj: existing.obj, mesh: existing.mesh, lod: existing.lod };
    const [txStr, tzStr] = key.split(',');
    const startDist = this.cameraDistanceToTile(Number(txStr), Number(tzStr));
    const transition: { startDist: number; sign: 1 | -1 } = {
      startDist,
      sign: lod > existing.lod ? 1 : -1,
    };
    const entry: LoadedTile = { obj, mesh: gltfMesh, lod, surfaceIds, prev: newPrev, transition };
    obj.ditherFade = 0;
    newPrev.obj.ditherFade = 0;
    this.loaded.set(key, entry);
  }

  private releaseTileSurfaces(tile: LoadedTile): void {
    this.releaseSurfaceIds(tile.surfaceIds);
  }

  private releaseSurfaceIds(ids: Set<string>): void {
    for (const sid of ids) {
      if (sid.length > 0) this.surfaceManager.release(sid);
    }
  }

  private lodForCurrentCamera(tx: number, tz: number): number {
    const dx = tx - this.lastCameraTileX;
    const dz = tz - this.lastCameraTileZ;
    return this.lodForDistance(Math.max(Math.abs(dx), Math.abs(dz)));
  }

  /**
   * Tear down every loaded tile, the shared material, the surface manager, and
   * the mesh-gen worker. The streamer is unusable afterward; callers must drop
   * their reference.
   */
  destroyAll(): void {
    for (const key of [...this.loaded.keys()]) this.evictTile(key);
    this.meshClient.destroy();
    this.sharedMaterial.destroy(this.scene.ctx);
    this.surfaceManager.destroy();
  }

  private evictTile(key: string): void {
    const tile = this.loaded.get(key);
    if (!tile) return;
    this.loaded.delete(key);
    this.releaseTileSurfaces(tile);
    this.scene.objectManager.removeObject(tile.obj);
    this.destroyTileIndexMap(tile.obj);
    tile.mesh.destroy(this.scene.ctx.gl);
    if (tile.prev) {
      this.scene.objectManager.removeObject(tile.prev.obj);
      this.destroyTileIndexMap(tile.prev.obj);
      tile.prev.mesh.destroy(this.scene.ctx.gl);
    }
  }

  private destroyTileIndexMap(obj: RenderObject): void {
    const gl = this.scene.ctx.gl;
    if (obj.terrainSurfaceIndices) {
      gl.deleteTexture(obj.terrainSurfaceIndices);
      obj.terrainSurfaceIndices = null;
      obj.terrainSurfaceIndicesTexture = null;
    }
    if (obj.terrainSurfaceWeight) {
      gl.deleteTexture(obj.terrainSurfaceWeight);
      obj.terrainSurfaceWeight = null;
      obj.terrainSurfaceWeightTexture = null;
    }
  }
}

